import type { Me } from '@morales/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { API_CACHE, DAY_CACHE, ME_CACHE, api } from './api';
import { OfflineBanner } from './components/ui';
import { useT } from './i18n';
import { qk } from './queryClient';

export type AuthStatus = 'loading' | 'signedIn' | 'signedOut' | 'error';

interface AuthValue {
  me: Me | null;
  status: AuthStatus;
  retry: () => void;
  signIn: (me: Me) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/** A sign-out made with no signal: the session is ended on the server the next time there is one (decision 182). */
const PENDING_LOGOUT = 'morales.pendingLogout';
const pendingLogout = () => { try { return localStorage.getItem(PENDING_LOGOUT) === '1'; } catch { return false; } };
const setPendingLogout = (on: boolean) => { try { if (on) localStorage.setItem(PENDING_LOGOUT, '1'); else localStorage.removeItem(PENDING_LOGOUT); } catch { /* storage blocked */ } };
/** The reads kept on the phone for no signal (vite.config.ts) belong to whoever was signed in: dropped when they are not. */
async function dropKeptReads() {
  try {
    if ('caches' in window) await Promise.all([API_CACHE, DAY_CACHE, ME_CACHE].map((c) => window.caches.delete(c)));
  } catch {
    // cache storage unavailable
  }
}

/** Who is signed in. A sign-out still owed to the server is sent first; until it goes, nobody is signed in here. */
async function whoIsIn(signal?: AbortSignal): Promise<Me | null> {
  if (pendingLogout()) {
    try {
      await api.logout();
      setPendingLogout(false);
    } catch {
      return null;
    }
  }
  const me = await api.me(signal);
  // signed out by the server (expired, or turned off by the owner): what was kept for them goes too
  if (me === null) await dropKeptReads();
  return me;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: qk.me,
    queryFn: ({ signal }) => whoIsIn(signal),
    networkMode: 'always', // fail fast offline instead of hanging on the splash screen
    staleTime: 5 * 60_000,
    retry: false,
  });
  const { data, isError, refetch } = query;

  const signIn = useCallback(
    (me: Me) => {
      setPendingLogout(false);
      // nothing kept for the last person on this phone is read as this one's
      void dropKeptReads();
      qc.setQueryData(qk.me, me);
    },
    [qc],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // signed out locally either way; the server is told when there is signal
      setPendingLogout(true);
    }
    qc.setQueryData(qk.me, null);
    try {
      // what one person typed on this device and did not save is not left for the next one (decision 165)
      localStorage.removeItem('morales.count.typed');
    } catch {
      // storage blocked
    }
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    await dropKeptReads();
  }, [qc]);

  const status: AuthStatus = data ? 'signedIn' : data === null ? 'signedOut' : isError ? 'error' : 'loading';

  const value = useMemo<AuthValue>(
    () => ({ me: data ?? null, status, retry: () => void refetch(), signIn, signOut }),
    [data, status, refetch, signIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Who is signed in, or null (also outside the provider): for what a screen keeps per person on the device. */
export function useMeId(): string | null {
  return useContext(AuthContext)?.me?.id ?? null;
}

/** Route guard: signed out (401) goes to /login and comes back here after the code is verified. */
export function RequireAuth({ children, portal = false }: { children: ReactNode; portal?: boolean }) {
  const { status, retry, me } = useAuth();
  const t = useT();
  const location = useLocation();

  if (status === 'signedIn') {
    // A customer only ever sees the portal; the business's people never land in it.
    if (me?.role === 'customer' && !portal) return <Navigate to="/portal" replace />;
    if (me && me.role !== 'customer' && portal) return <Navigate to="/" replace />;
    return <>{children}</>;
  }
  if (status === 'signedOut') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return (
    <div className="splash">
      <OfflineBanner />
      <div className="splash-body">
        <div className="brand-mark">{t('appName')}</div>
        {status === 'loading' ? (
          <p role="status">{t('loading')}</p>
        ) : (
          <>
            <p role="alert">{t('error')}</p>
            <button type="button" className="btn btn-primary btn-lg" onClick={retry}>
              {t('retry')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
